import React, { useState } from "react";
import { API_ENDPOINTS } from "../config";
import { useApp } from "../context/AppContext";
import { VocreaLogo } from "./VocreaLogo";
import { ImageWithFallback } from "./ImageWithFallback";
import "../styles/profile-import-new.css";

interface ProfileImportScreenProps {
  userType: string;
  onBack: () => void;
  onManualEntry: () => void;
}

type ImportState = "input" | "scraping" | "success" | "error";

const ProfileImportScreenNew: React.FC<ProfileImportScreenProps> = ({
  userType,
  onBack,
  onManualEntry,
}) => {
  const { dispatch } = useApp();
  const [importState, setImportState] = useState<ImportState>("input");
  const [profileUrl, setProfileUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [scrapedData, setScrapedData] = useState<any>(null);

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    if (!url.startsWith("http")) return false;
    if (!url.includes("psychologytoday.com")) return false;
    return true;
  };

  const handleImport = async () => {
    if (!validateUrl(profileUrl)) {
      setErrorMessage("Please enter a valid Psychology Today profile URL");
      return;
    }

    setImportState("scraping");
    setErrorMessage("");

    try {
      const response = await fetch(API_ENDPOINTS.SCRAPE_PSYCHOLOGY_TODAY, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          profile_url: profileUrl,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to import profile");
      }

      setScrapedData(data.data);
      setImportState("success");
    } catch (error: any) {
      console.error("Import failed:", error);
      setErrorMessage(error.message || "Failed to import profile");
      setImportState("error");
    }
  };

  // Helper function to parse location string into structured data
  const parseLocation = (locationStr: string) => {
    if (!locationStr) return null;

    // Parse "Brooklyn, NY 10016" → {city: "Brooklyn", state: "NY", postal_code: "10016"}
    const parts = locationStr.split(',').map(s => s.trim());
    if (parts.length >= 2) {
      // Second part is "NY 10016" - split by space to separate state and postal code
      const stateAndZip = parts[1].split(' ').filter(s => s.length > 0);
      const state = stateAndZip[0] || parts[1]; // First part is state (e.g., "NY")
      const postalCode = stateAndZip[1] || null; // Second part is postal code if present

      return {
        city: parts[0],
        state: state,
        postal_code: postalCode,
        in_person: true,
        is_primary: true
      };
    }
    return null;
  };

  const handleConfirmImport = async () => {
    try {
      // Note: At this point, the user is already registered and verified
      // We should update their profile with the imported data, not re-register

      // Get auth token (user should be logged in at this point)
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication required. Please log in.");
      }

      // Update the user's profile with the imported data from scrapedData
      if (!scrapedData) {
        throw new Error("No profile data to import");
      }

      // Parse location if available
      const parsedLocation = scrapedData.location ? parseLocation(scrapedData.location) : null;

      // Call profile update endpoint with scraped profile data
      const updateRes = await fetch(API_ENDPOINTS.UPDATE_THERAPIST_PROFILE, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          profile_url: scrapedData.profile_url,
          name: scrapedData.name,
          title: scrapedData.title,
          phone: scrapedData.phone,
          self_description: scrapedData.self_description,
          years_practicing: scrapedData.years_practicing,
          specialties: scrapedData.specialties,
          treatment_approach: scrapedData.treatment_approach,
          education: scrapedData.education,
          languages: scrapedData.languages,
          insurance_accepted: scrapedData.insurance_accepted,
          session_fee: scrapedData.session_fee,
          photo_urls: scrapedData.photo_urls,
          locations: parsedLocation ? [parsedLocation] : [],
        }),
      });
      const updateData = await updateRes.json();
      if (!updateRes.ok) {
        throw new Error(updateData.error || "Failed to update profile");
      }

      // Fetch user detailed information
      const userInfoRes = await fetch(API_ENDPOINTS.USERS_INFO, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const userInfoData = await userInfoRes.json();
      if (!userInfoRes.ok) {
        throw new Error(userInfoData.error || "Failed to get user information");
      }

      // The user ID must come from the trusted server response
      const userId = userInfoData.id;
      if (!userId) {
        throw new Error("User ID not found in server response");
      }

      // Set user information in AppContext
      dispatch({
        type: "SET_USER",
        payload: {
          id: userId,
          name: userInfoData.name || "user",
          email: userInfoData.email,
          role: (userInfoData.type as "therapist" | "normal") || "normal",
          has_paid: userInfoData.has_paid || false,
          profile_data: userInfoData.profile_data,
          profile_photo_url: userInfoData.profile_photo_url,
        },
      });
      dispatch({ type: "SET_CURRENT_USER", payload: userId });

      // Clear temporary storage
      localStorage.removeItem("temp_register_email");
      localStorage.removeItem("temp_register_password");
      localStorage.removeItem("temp_register_name");

      // Navigate to the main therapist dashboard
      dispatch({
        type: "SET_SCREEN",
        payload: userType === "therapist" ? "main-provider" : "main",
      });
    } catch (error: any) {
      console.error("Profile import failed:", error);
      setErrorMessage(error.message || "Failed to import profile");
      setImportState("error");
    }
  };

  const renderInputState = () => (
    <>
      {/* Import from Psychology Today Section */}
      <div className="import-section-highlighted">
        <div className="import-section-header">
          <h3 className="import-section-title">Import from Psychology Today</h3>
          <p className="import-section-description">
            Recommended - automatically imports your complete profile
          </p>
        </div>

        <div className="url-input-container">
          <label htmlFor="profileUrl" className="url-input-label">
            Enter your profile URL:
          </label>
          <input
            type="url"
            id="profileUrl"
            value={profileUrl}
            onChange={(e) => setProfileUrl(e.target.value)}
            placeholder="https://www.psychologytoday.com/us/therapists/..."
            className="url-input"
          />
          <p className="url-input-help">
            Example: https://www.psychologytoday.com/us/therapists/jane-smith-new-york-ny/123456
          </p>
        </div>

        <div className="import-benefits">
          <div className="benefit-item">
            <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Automatically imports all profile data</span>
          </div>
          <div className="benefit-item">
            <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Faster setup (30 seconds)</span>
          </div>
          <div className="benefit-item">
            <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>You can edit everything after import</span>
          </div>
        </div>

        <button
          onClick={handleImport}
          className="import-button-primary"
          disabled={!validateUrl(profileUrl)}
        >
          Import Profile
        </button>
      </div>

      {/* Divider */}
      <div className="import-divider">
        <div className="import-divider-line"></div>
        <span className="import-divider-text">OR</span>
        <div className="import-divider-line"></div>
      </div>

      {/* Manual Entry Section */}
      <div className="import-section">
        <div className="import-section-header">
          <h3 className="import-section-title">Enter information manually</h3>
          <p className="import-section-description">
            If you don't have a Psychology Today profile
          </p>
        </div>

        <button onClick={onManualEntry} className="import-button-secondary">
          Continue to Questionnaire
        </button>
      </div>
    </>
  );

  const renderScrapingState = () => (
    <div className="import-status-container">
      <div className="import-spinner"></div>
      <h3 className="import-status-title">Importing your profile...</h3>
      <p className="import-status-text">Fetching data from Psychology Today</p>
      <p className="import-status-note">Please wait 10-30 seconds</p>
    </div>
  );

  const renderSuccessState = () => (
    <div className="import-success-container">
      <div className="success-icon-circle">✓</div>
      <h3 className="import-success-title">Profile imported successfully!</h3>

      <div className="profile-preview-card">
        <h4 className="preview-title">Preview:</h4>
        <div className="preview-details">
          <p>
            <strong>Name:</strong> {scrapedData?.name || "N/A"}
          </p>
          <p>
            <strong>Title:</strong> {scrapedData?.title || "N/A"}
          </p>
          <p>
            <strong>Location:</strong> {scrapedData?.location || "N/A"}
          </p>
          {scrapedData?.specialties && (
            <div>
              <strong>Specialties:</strong>
              {scrapedData.specialties["Top Specialties"] && (
                <p className="specialties-list">
                  {scrapedData.specialties["Top Specialties"].join(", ")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="import-actions">
        <button onClick={() => setImportState("input")} className="import-button-secondary">
          Start Over
        </button>
        <button onClick={handleConfirmImport} className="import-button-primary">
          Confirm & Create Account
        </button>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="import-error-container">
      <div className="error-icon-circle">✗</div>
      <h3 className="import-error-title">Unable to import profile</h3>
      <p className="import-error-message">{errorMessage}</p>

      <div className="error-reasons-card">
        <p className="error-reasons-title">Possible reasons:</p>
        <ul className="error-reasons-list">
          <li>URL is not accessible</li>
          <li>Profile is private or doesn't exist</li>
          <li>Invalid URL format</li>
        </ul>
      </div>

      <div className="import-actions">
        <button onClick={() => setImportState("input")} className="import-button-secondary">
          Try Different URL
        </button>
        <button onClick={onManualEntry} className="import-button-primary">
          Enter Manually Instead
        </button>
      </div>
    </div>
  );

  return (
    <div className="profile-import-new-screen">
      {/* Left Side - Import Form */}
      <div className="profile-import-new-left">
        <div className="profile-import-new-form-container">
          {/* Back Button and Header */}
          <div className="profile-import-new-header">
            <button onClick={onBack} className="import-back-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Back</span>
            </button>

            <div className="import-header-content">
              <div className="import-logo-title">
                <VocreaLogo />
                <h1 className="import-app-title">Vocrea AI</h1>
              </div>
              <h2 className="import-page-title">Set Up Your Profile</h2>
              <p className="import-page-subtitle">Choose how you'd like to get started</p>
            </div>
          </div>

          {/* Content based on state */}
          <div className="import-content-area">
            {importState === "input" && renderInputState()}
            {importState === "scraping" && renderScrapingState()}
            {importState === "success" && renderSuccessState()}
            {importState === "error" && renderErrorState()}
          </div>
        </div>
      </div>

      {/* Right Side - Marketing Message */}
      <div className="profile-import-new-right">
        <ImageWithFallback
          src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1200&auto=format&fit=crop"
          alt="Background"
          className="import-background-image"
        />
        <div className="import-marketing-overlay">
          <div className="import-marketing-content">
            <h2 className="import-marketing-title">
              Join our network of trusted professionals
            </h2>
            <div className="import-marketing-description">
              <p>
                Connect with clients seeking compassionate, voice-first emotional support.
              </p>
              <p>
                Our platform makes it easy to manage your practice and reach those who need you most.
              </p>
            </div>
            <div className="import-feature-cards">
              <div className="import-feature-card">
                <div className="import-feature-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </svg>
                </div>
                <div className="import-feature-text">
                  <div className="import-feature-title">Flexible</div>
                  <div className="import-feature-subtitle">Schedule</div>
                </div>
              </div>
              <div className="import-feature-card">
                <div className="import-feature-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="8" r="7" />
                    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                  </svg>
                </div>
                <div className="import-feature-text">
                  <div className="import-feature-title">Verified</div>
                  <div className="import-feature-subtitle">Credentials</div>
                </div>
              </div>
              <div className="import-feature-card">
                <div className="import-feature-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <div className="import-feature-text">
                  <div className="import-feature-title">Growing</div>
                  <div className="import-feature-subtitle">Client Base</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileImportScreenNew;
